import { useCallback, useEffect, useState } from 'react'
import { FilterDescriptor, SortDescriptor } from '@progress/kendo-data-query'
import { TextFilter } from '@progress/kendo-react-data-tools'
import { GridColumn as Column, Grid, GridSortChangeEvent } from '@progress/kendo-react-grid'
import { PageData } from 'api/Pagination/Types'
import {
  PlatformTransactionDto,
  PlatformTransactionFilterKey,
} from 'api/Trading-Accounts/Types/PlatformTransactionDto'
import { PlatformTransactionTypeDto } from 'api/Trading-Accounts/Types/PlatformTransactionTypeDto'
import { Pager } from 'components/components/Pager'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import { CustomDateFilter } from 'components/Filter/common.filter'
import { SelectFilter } from 'components/Filter/SelectFilter'
import { TradingAccountSymbolFilter } from 'components/Filter/TradingAccountFields/TradingAccountSymbolFilter'
import { GridLoading } from 'components/GridLoading'
import { Spinner } from 'components/Spinner'
import { createGridState, useGridState, useGridStatusState } from 'features/DataGrid'
import { CompositeFilterDescriptor } from 'store/grid'
import { GridFilterOperatorEnum, GridFilterOperatorNameEnum } from 'types/GridFilterOperatorEnum'
import { PageQuery } from 'utils/api'
import { getAllowedTransactionTypes } from 'utils/getAllowedTransactionTypes'
import { buildQuery, BuildQueryProps, buildSortQuery } from 'utils/QueryHelpers'
import { TableWithExport } from 'utils/table/withExport'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import tradingAccountPlatformTransactionDetailsColumn from './tradingAccountPlatfromTransactionDetailsColumn'

interface TradingAccountPlatformTransactionDetailsTableProps {
  platformTransactionTypes: PlatformTransactionTypeDto[]
  platformTransactions: PageData<PlatformTransactionDto[]> | undefined
  gridQuery: PageQuery
  isLoading: boolean
  isFetching?: boolean
  onChange: (pageQuery: PageQuery) => void
}

const TradingAccountPlatformTransactionDetailsTable: React.FC<
  TradingAccountPlatformTransactionDetailsTableProps
> = (props) => {
  const {
    platformTransactionTypes,
    platformTransactions,
    gridQuery,
    isLoading,
    isFetching,
    onChange,
  } = props

  const [tableData, setTableData] = useState<TableStateContextData>({
    skipCount: true,
    meta: {
      totalResults: platformTransactions?.totalResults || 0,
      itemsCount: platformTransactions?.itemsCount ?? 0,
      pageSize: 10,
      items: platformTransactions?.items ?? [],
      pageIndex: 1,
      totalPages: platformTransactions?.totalPages ?? 1,
    },
  })
  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()

  useEffect(() => {
    gridActions.setGridFilters(createGridState({ skip: 0 }, gridState))
  }, [])

  useEffect(() => {
    if (isFetching !== undefined) {
      gridStatusActions.setFetching(isFetching)
    }
  }, [isFetching])

  useEffect(() => {
    if (platformTransactions) {
      setTableData((prevState) => ({
        ...prevState,
        meta: {
          ...platformTransactions,
          pageSize: gridState.pageSize || 10,
          pageIndex: gridState.pageIndex || 1,
          itemsCount: platformTransactions.items?.length || 0,
        },
      }))
    }
  }, [platformTransactions])

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(normalizePlatformTransactionFilter(filter ?? gridState.filter)) ?? null
    },
    [gridState.filter]
  )

  const getSort = useCallback((sortDescriptors: SortDescriptor[]): string | null => {
    return buildSortQuery(sortDescriptors)
  }, [])

  const handleGridSortChange = async (event: GridSortChangeEvent) => {
    gridStatusActions.setFetching(true)
    gridActions.setGridFilters({
      sort: event.sort,
    })
    onChange({
      ...gridQuery,
      sort: getSort(event.sort),
    })
  }

  const handlePageChange = async (pageIndex: number) => {
    gridStatusActions.setFetching(true)
    const take = gridState.take || 10
    const skip = (pageIndex - 1) * take

    gridActions.setGridFilters({
      ...gridState,
      take,
      skip,
      pageIndex,
      pageSize: take,
    })

    onChange({
      ...gridQuery,
      pageIndex,
      pageSize: take,
      skip,
      skipCount: tableData?.skipCount,
    })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    gridStatusActions.setFetching(true)
    const currentPageIndex = gridState.pageIndex || 1
    const skip = (currentPageIndex - 1) * pageSize

    gridActions.setGridFilters({
      ...gridState,
      pageIndex: currentPageIndex,
      pageSize,
      take: pageSize,
      skip,
    })

    onChange({
      ...gridQuery,
      pageIndex: currentPageIndex,
      pageSize,
      skip,
      skipCount: tableData?.skipCount,
    })
  }
  const handleSkipCountChange = async (skipCount: boolean) => {
    gridStatusActions.setFetching(true)
    const currentPageIndex = gridState.pageIndex || 1
    const currentPageSize = gridState.pageSize || 10
    const take = currentPageSize
    const skip = (currentPageIndex - 1) * take

    setTableData((prevState) => ({
      ...prevState,
      skipCount,
    }))

    onChange({
      ...gridQuery,
      pageIndex: currentPageIndex,
      pageSize: currentPageSize,
      skip,
      take,
      skipCount,
    })
  }

  const handleFilterFetch = async (params?: { query?: string | null }) => {
    gridStatusActions.setFetching(true)
    onChange({
      ...gridQuery,
      search: params?.query ?? getQuery(),
      pageIndex: 1,
    })
  }

  const TradingAccountPlatformTransactionTypeFilter = ({
    filter,
    onFilterChange,
  }: {
    filter: FilterDescriptor
    onFilterChange: (event: { nextFilter: FilterDescriptor }) => void
  }) => {
    const data = getAllowedTransactionTypes(platformTransactionTypes).map((x) => ({
      id: x.id,
      label: x.name,
      value: x.id,
    }))

    return <SelectFilter filter={filter} onFilterChange={onFilterChange} data={data} />
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <>
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={handleFilterFetch}
        filterType={FilterType.PLATFORM_TRANSACTIONS}
        filterFields={[
          {
            name: 'Ticket',
            label: 'Ticket',
            filter: TextFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
            ],
          },
          {
            name: 'Type',
            label: 'Type',
            filter: TradingAccountPlatformTransactionTypeFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
            ],
          },
          {
            name: PlatformTransactionFilterKey.symbol,
            label: 'Symbol',
            filter: TradingAccountSymbolFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.equalOperator,
                operator: GridFilterOperatorEnum.equalOperator,
              },
              {
                text: GridFilterOperatorNameEnum.notEqOperator,
                operator: GridFilterOperatorEnum.notEqOperator,
              },
            ],
          },
          {
            name: 'OpenTime',
            label: 'Open Time',
            filter: CustomDateFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.afterOperator,
                operator: GridFilterOperatorEnum.afterOperator,
              },
              {
                text: GridFilterOperatorNameEnum.afterOrEqualOperator,
                operator: GridFilterOperatorEnum.afterOrEqualOperator,
              },
              {
                text: GridFilterOperatorNameEnum.beforeOrEqualOperator,
                operator: GridFilterOperatorEnum.beforeOrEqualOperator,
              },
              {
                text: GridFilterOperatorNameEnum.beforeOperator,
                operator: GridFilterOperatorEnum.beforeOperator,
              },
            ],
          },
          {
            name: 'CloseTime',
            label: 'Close Time',
            filter: CustomDateFilter,
            operators: [
              {
                text: GridFilterOperatorNameEnum.afterOperator,
                operator: GridFilterOperatorEnum.afterOperator,
              },
              {
                text: GridFilterOperatorNameEnum.afterOrEqualOperator,
                operator: GridFilterOperatorEnum.afterOrEqualOperator,
              },
              {
                text: GridFilterOperatorNameEnum.beforeOrEqualOperator,
                operator: GridFilterOperatorEnum.beforeOrEqualOperator,
              },
              {
                text: GridFilterOperatorNameEnum.beforeOperator,
                operator: GridFilterOperatorEnum.beforeOperator,
              },
            ],
          },
        ]}
        getQuery={getQuery}
      />
      <TableStateContext.Provider value={[tableData, setTableData]}>
        <GridLoading isLoading={isFetching!}>
          {platformTransactions && (
            <TableWithExport data={platformTransactions.items || []}>
              <Grid
                resizable
                sortable={{
                  allowUnsort: true,
                  mode: 'multiple',
                }}
                take={gridState.take}
                skip={gridState.skip}
                sort={gridState.sort}
                total={platformTransactions.totalResults}
                data={platformTransactions.items}
                onSortChange={handleGridSortChange}
                pageable
                pager={() => (
                  <Pager
                    onSkipCountChange={handleSkipCountChange}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    page={gridState.pageIndex || 1}
                    pageSize={gridState.pageSize || 10}
                    take={gridState.take || 10}
                    skip={gridState.skip || 0}
                  />
                )}
                style={{ zIndex: 0 }}
              >
                {tradingAccountPlatformTransactionDetailsColumn.map((column, index) => (
                  <Column
                    key={index}
                    field={column.field}
                    title={column.title}
                    filter={column.filter}
                    sortable={column.sortable}
                    headerCell={column.headerCell}
                    cell={column.cell}
                    children={column.children}
                    className={column.className}
                    width={column.width}
                    minResizableWidth={column.minResizableWidth}
                  />
                ))}
              </Grid>
            </TableWithExport>
          )}
        </GridLoading>
      </TableStateContext.Provider>
    </>
  )
}

export const normalizePlatformTransactionFilter = (
  filter: FilterDescriptor[] | CompositeFilterDescriptor
): BuildQueryProps => {
  if (!filter) return { logic: 'and', filters: [] }

  const normalizeItem = (
    item: FilterDescriptor | CompositeFilterDescriptor
  ): FilterDescriptor | CompositeFilterDescriptor => {
    if ('filters' in item) {
      return {
        ...item,
        filters: item.filters.map(normalizeItem),
      }
    }

    if (item.field === 'OpenTimeFrom' || item.field === 'OpenTimeTo') {
      return {
        ...item,
        field: PlatformTransactionFilterKey.openTime,
      }
    }

    return item
  }

  if (Array.isArray(filter)) {
    return {
      logic: 'and',
      filters: filter.map(normalizeItem),
    }
  }

  return {
    ...filter,
    filters: filter.filters.map(normalizeItem),
  }
}

export default TradingAccountPlatformTransactionDetailsTable
